import { create } from 'zustand';

/**
 * Progress state for the connection-restore drainer
 * (`services/pendingSyncDrainer`). Read by `PendingSyncBanner` to show
 * the doctor "processing 3 of 10 recordings…" while the queue drains
 * after a flaky-internet stretch.
 *
 * The drainer ONLY mutates this store; subscribers must not call its
 * setters directly. Everything is purely informational — the canonical
 * state lives in `recording_sessions` rows + IDB.
 */

export type PendingSyncJobStatus =
  | 'queued'
  | 'uploading'
  | 'reprocessing'
  | 'done'
  | 'failed';

export interface PendingSyncJob {
  sessionId: string;
  patientName?: string;
  status: PendingSyncJobStatus;
  /** Server-typed code on failure (AUDIO_NOT_ON_SERVER, SYNTH_ABANDONED, …). */
  errorCode?: string;
  errorMessage?: string;
  /** ms epoch — when this job entered the active queue. */
  startedAt: number;
  /** Set when status flips to done/failed. */
  finishedAt?: number;
}

interface PendingSyncState {
  /** True while the drainer pass is actively running. */
  active: boolean;
  /** Sessions ordered by enqueue time (FIFO). */
  jobs: Record<string, PendingSyncJob>;

  beginPass(): void;
  endPass(): void;
  enqueue(job: Omit<PendingSyncJob, 'startedAt' | 'status'> & { status?: PendingSyncJobStatus }): void;
  setStatus(sessionId: string, status: PendingSyncJobStatus): void;
  fail(sessionId: string, errorCode: string | undefined, errorMessage: string): void;
  complete(sessionId: string): void;
  /** Drop a job from view — UI usually does this on the row's dismiss
   *  click after a terminal status. */
  dismiss(sessionId: string): void;
  /** Wipe everything — used on logout. */
  reset(): void;
}

export const usePendingSyncStore = create<PendingSyncState>((set) => ({
  active: false,
  jobs: {},

  beginPass() {
    set({ active: true });
  },
  endPass() {
    set({ active: false });
  },
  enqueue(init) {
    set((state) => ({
      jobs: {
        ...state.jobs,
        [init.sessionId]: {
          sessionId: init.sessionId,
          patientName: init.patientName,
          errorCode: init.errorCode,
          errorMessage: init.errorMessage,
          status: init.status ?? 'queued',
          startedAt: Date.now(),
          finishedAt: undefined,
        },
      },
    }));
  },
  setStatus(sessionId, status) {
    set((state) => {
      const job = state.jobs[sessionId];
      if (!job) return state;
      return {
        jobs: {
          ...state.jobs,
          [sessionId]: { ...job, status },
        },
      };
    });
  },
  fail(sessionId, errorCode, errorMessage) {
    set((state) => {
      const job = state.jobs[sessionId];
      if (!job) return state;
      return {
        jobs: {
          ...state.jobs,
          [sessionId]: {
            ...job,
            status: 'failed',
            errorCode,
            errorMessage,
            finishedAt: Date.now(),
          },
        },
      };
    });
  },
  complete(sessionId) {
    set((state) => {
      const job = state.jobs[sessionId];
      if (!job) return state;
      return {
        jobs: {
          ...state.jobs,
          [sessionId]: {
            ...job,
            status: 'done',
            finishedAt: Date.now(),
          },
        },
      };
    });
  },
  dismiss(sessionId) {
    set((state) => {
      const next = { ...state.jobs };
      delete next[sessionId];
      return { jobs: next };
    });
  },
  reset() {
    set({ active: false, jobs: {} });
  },
}));

/**
 * Selector helpers.
 *
 * IMPORTANT: `selectAllJobs` returns a fresh array reference every call
 * (`Object.values(...).sort(...)`). Pass it through `useShallow` from
 * `zustand/react/shallow` when subscribing, or React 18's
 * useSyncExternalStore cached-snapshot check fails and the component
 * infinite-loops with "Maximum update depth exceeded" (#185 — exactly
 * the bug that crashed the EMR on login before this guard was added).
 *
 *   const jobs = usePendingSyncStore(useShallow(selectAllJobs)); // ✅
 *   const jobs = usePendingSyncStore(selectAllJobs);             // 💥
 *
 * `selectActiveCount` and `selectTotalCount` return primitives, so they
 * do NOT need useShallow.
 */
export const selectActiveCount = (state: PendingSyncState): number =>
  Object.values(state.jobs).filter(
    (j) => j.status === 'queued' || j.status === 'uploading' || j.status === 'reprocessing',
  ).length;

export const selectTotalCount = (state: PendingSyncState): number =>
  Object.keys(state.jobs).length;

export const selectAllJobs = (state: PendingSyncState): PendingSyncJob[] =>
  Object.values(state.jobs).sort((a, b) => a.startedAt - b.startedAt);
